import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, shareReplay } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { StoredTabData } from '../../../state/app.state';

@Injectable({ providedIn: 'root' })
export class TabsService {
  private loadCache$?: Observable<StoredTabData[]>;
  private hasLoadedOnce = false;
  private isLoading = false;

  constructor(private http: HttpClient) {}

  clearCache(): void {
    this.loadCache$ = undefined;
    this.hasLoadedOnce = false;
    this.isLoading = false;
  }

  hasLoadedTabsOnce(): boolean {
    return this.hasLoadedOnce;
  }

  setHasLoadedOnce(): void {
    this.hasLoadedOnce = true;
  }

  load(): Observable<StoredTabData[]> {
    // If cache exists, return it immediately
    if (this.loadCache$) {
      return this.loadCache$;
    }
    
    // If already loaded once, don't load again
    if (this.hasLoadedOnce) {
      return of([]);
    }
    
    // If currently loading, create a pending observable to share
    if (this.isLoading) {
      // This shouldn't happen, but if it does, return empty
      return of([]);
    }
    
    const token = localStorage.getItem('current_user_token');
    if (!token) {
      return of([]);
    }
    
    // CRITICAL: Set flags synchronously BEFORE creating observable
    // This prevents race conditions when multiple components call load() simultaneously
    this.isLoading = true;
    this.hasLoadedOnce = true;
    
    // Create and cache the observable
    this.loadCache$ = this.http.get<any>('/backend/user_tabs/all', {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      map(response => {
        this.isLoading = false;
        return response.payload || [];
      }),
      catchError(err => {
        this.isLoading = false;
        console.error('Failed to load tabs:', err);
        return of([]);
      }),
      shareReplay(1) // Share the result with all subscribers
    );
    
    return this.loadCache$;
  }

  createTab(tab: StoredTabData): Observable<void> {
    const token = localStorage.getItem('current_user_token');
    if (!token) {
      return of(undefined);
    }
    return this.http.post<void>('/backend/user_tabs/create', tab, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      catchError(err => {
        console.error('Failed to create tab:', err);
        return of(undefined);
      })
    );
  }

  updateTab(tabId: string, tab: StoredTabData): Observable<void> {
    const token = localStorage.getItem('current_user_token');
    if (!token) {
      return of(undefined);
    }
    return this.http.put<void>(`/backend/user_tabs/update/${tabId}`, tab, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      catchError(err => {
        console.error('Failed to update tab:', err);
        return of(undefined);
      })
    );
  }

  deleteTab(tabId: string): Observable<void> {
    const token = localStorage.getItem('current_user_token');
    if (!token) {
      return of(undefined);
    }
    return this.http.delete<void>(`/backend/user_tabs/delete/${tabId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      catchError(err => {
        console.error(`Failed to delete tab ${tabId}:`, err);
        return of(undefined);
      })
    );
  }
}


